// src/pages/billing/BillingDashboard.js
// TO-8: AMC Due section + AMC Contracted Value card
// TO-8 v2: freqLabel removed (warning fix), AMC summary card added

import { useEffect, useState, useCallback } from "react";
import {
  collection, query, where, getDocs,
  doc, updateDoc, serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useNavigate } from "react-router-dom";

export default function BillingDashboard() {
  const user       = JSON.parse(localStorage.getItem("user") || "{}");
  const company_id = user.company_id || "";
  const navigate   = useNavigate();

  const [stats, setStats] = useState({
    totalRevenue: 0, pendingPayments: 0,
    paidInvoices: 0, overdueInvoices: 0,
    totalIncome: 0,  totalExpense: 0,
  });
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loading,        setLoading]        = useState(true);

  // ── AMC state ─────────────────────────────────────────────────────────────
  const [amcDue,        setAmcDue]        = useState([]);
  const [amcSoon,       setAmcSoon]       = useState([]);
  const [amcLoading,    setAmcLoading]    = useState(true);
  // ── AMC contracted value summary ──────────────────────────────────────────
  const [amcSummary,    setAmcSummary]    = useState({
    activeContracts: 0,
    monthlyValue:    0,   // sum of all active contract amounts
    dueThisMonth:    0,   // sum of due/soon contracts
    dueCount:        0,
  });

  // ── Load billing data ─────────────────────────────────────────────────────
  const fetchBillingData = useCallback(async () => {
    try {
      setLoading(true);
      const invoiceSnap = await getDocs(
        query(collection(db, "invoices"), where("company_id", "==", company_id))
      );
      let totalRevenue = 0, pendingPayments = 0, paidInvoices = 0, overdueInvoices = 0;
      const invoices = [];
      invoiceSnap.forEach(d => {
        const data = { id: d.id, ...d.data() };
        invoices.push(data);
        totalRevenue += Number(data.total_amount || 0);
        if (data.payment_status === "Paid")    paidInvoices++;
        if (data.payment_status === "Overdue") overdueInvoices++;
        if (data.payment_status === "Pending" || data.payment_status === "Partial")
          pendingPayments += Number(data.balance_amount || 0);
      });
      invoices.sort((a, b) => (b.invoice_date?.seconds || 0) - (a.invoice_date?.seconds || 0));

      const ftSnap = await getDocs(
        query(collection(db, "financial_transactions"), where("company_id", "==", company_id))
      );
      let totalIncome = 0, totalExpense = 0;
      ftSnap.forEach(d => {
        const data = d.data();
        if (data.type === "income")   totalIncome  += Number(data.amount || 0);
        if (data.type === "expense" || data.type === "transport")
          totalExpense += Number(data.amount || 0);
      });
      setStats({ totalRevenue, pendingPayments, paidInvoices, overdueInvoices, totalIncome, totalExpense });
      setRecentInvoices(invoices.slice(0, 10));
    } catch (err) {
      console.error("Billing dashboard error:", err);
    } finally { setLoading(false); }
  }, [company_id]);

  // ── Load AMC contracts ────────────────────────────────────────────────────
  const fetchAMCDue = useCallback(async () => {
    if (!company_id) return;
    try {
      setAmcLoading(true);
      const snap = await getDocs(query(
        collection(db, "amc_contracts"),
        where("company_id", "==", company_id),
        where("status",     "==", "active")
      ));

      const today = new Date(); today.setHours(0,0,0,0);
      const in7   = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

      const due = [], soon = [], expiries = [];
      let monthlyValue = 0, dueThisMonth = 0, dueCount = 0, activeContracts = 0;

      snap.forEach(d => {
        const c = { id: d.id, ...d.data() };

        // Auto-expire
        if (c.end_date) {
          const end = new Date(c.end_date); end.setHours(0,0,0,0);
          if (end < today) {
            expiries.push(updateDoc(doc(db, "amc_contracts", d.id), {
              status: "expired", updated_at: serverTimestamp(),
            }));
            return;
          }
        }

        // Active contract — add to monthly value
        activeContracts++;
        monthlyValue += Number(c.amount || 0);

        if (!c.next_invoice_date) return;
        const next = new Date(c.next_invoice_date); next.setHours(0,0,0,0);

        if (next <= today) {
          due.push(c);
          dueThisMonth += Number(c.amount || 0);
          dueCount++;
        } else if (next <= in7) {
          soon.push(c);
          dueThisMonth += Number(c.amount || 0);
          dueCount++;
        }
      });

      if (expiries.length > 0) {
        Promise.all(expiries).catch(e => console.warn("[BillingDashboard] AMC expire:", e));
      }

      setAmcDue(due);
      setAmcSoon(soon);
      setAmcSummary({ activeContracts, monthlyValue, dueThisMonth, dueCount });
    } catch (err) {
      console.error("[BillingDashboard] fetchAMCDue error:", err);
    } finally { setAmcLoading(false); }
  }, [company_id]);

  useEffect(() => {
    if (company_id) { fetchBillingData(); fetchAMCDue(); }
  }, [company_id, fetchBillingData, fetchAMCDue]);

  const statusBadge = (s) => ({
    Paid:    "bg-green-100 text-green-700",
    Pending: "bg-yellow-100 text-yellow-700",
    Partial: "bg-blue-100 text-blue-700",
    Overdue: "bg-red-100 text-red-700",
    Draft:   "bg-gray-100 text-gray-700",
  }[s] || "bg-gray-100 text-gray-700");

  return (
    <div className="p-6 space-y-6">

      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Billing Dashboard</h1>
        <button onClick={() => navigate("/billing/create")}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700">
          + Create Invoice
        </button>
      </div>

      {/* Invoice Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard title="Total Revenue"    value={`₹${stats.totalRevenue.toLocaleString()}`}    color="blue" />
        <StatCard title="Pending Payments" value={`₹${stats.pendingPayments.toLocaleString()}`} color="yellow" />
        <StatCard title="Paid Invoices"    value={stats.paidInvoices}                            color="green" />
        <StatCard title="Overdue"          value={stats.overdueInvoices}                         color="red" />
        <StatCard title="Total Income"     value={`₹${stats.totalIncome.toLocaleString()}`}      color="green" />
        <StatCard title="Total Expense"    value={`₹${stats.totalExpense.toLocaleString()}`}     color="red" />
      </div>

      {/* P&L */}
      <div className="bg-white rounded-xl shadow p-4 flex gap-6 items-center flex-wrap">
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide">Net P&L</p>
          <p className={`text-2xl font-bold ${stats.totalIncome - stats.totalExpense >= 0 ? "text-green-600" : "text-red-600"}`}>
            ₹{(stats.totalIncome - stats.totalExpense).toLocaleString()}
          </p>
        </div>
        <div className="h-10 w-px bg-gray-200" />
        <div className="text-sm text-gray-500">
          Income: <span className="font-semibold text-green-600">₹{stats.totalIncome.toLocaleString()}</span>
          &nbsp;&nbsp;Expense: <span className="font-semibold text-red-600">₹{stats.totalExpense.toLocaleString()}</span>
        </div>
      </div>

      {/* ── AMC Contracted Value Card ─────────────────────────────────────── */}
      {!amcLoading && amcSummary.activeContracts > 0 && (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4">
          <div className="flex justify-between items-start flex-wrap gap-3">
            <div>
              <p className="text-xs font-semibold text-purple-600 uppercase tracking-wide mb-1">
                🔄 AMC Contracted Revenue
              </p>
              <p className="text-xs text-purple-500 mt-0.5">
                {amcSummary.activeContracts} active contract{amcSummary.activeContracts !== 1 ? "s" : ""} —
                not yet invoiced, committed income
              </p>
            </div>
            <button onClick={() => navigate("/amc-contracts")}
              className="text-purple-600 text-xs hover:underline font-medium">
              Manage →
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
            {/* Total active contract value */}
            <div className="bg-white rounded-xl p-3 border border-purple-100">
              <p className="text-xs text-gray-400 uppercase tracking-wide">Total Contracted</p>
              <p className="text-xl font-bold text-purple-700 mt-1">
                ₹{amcSummary.monthlyValue.toLocaleString("en-IN")}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">per billing cycle</p>
            </div>

            {/* Due this week */}
            <div className={`bg-white rounded-xl p-3 border ${amcSummary.dueCount > 0 ? "border-red-200" : "border-purple-100"}`}>
              <p className="text-xs text-gray-400 uppercase tracking-wide">Due / Due Soon</p>
              <p className={`text-xl font-bold mt-1 ${amcSummary.dueCount > 0 ? "text-red-600" : "text-gray-400"}`}>
                ₹{amcSummary.dueThisMonth.toLocaleString("en-IN")}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                {amcSummary.dueCount > 0
                  ? `${amcSummary.dueCount} invoice${amcSummary.dueCount !== 1 ? "s" : ""} pending`
                  : "All invoiced"}
              </p>
            </div>

            {/* Active contracts count */}
            <div className="bg-white rounded-xl p-3 border border-purple-100">
              <p className="text-xs text-gray-400 uppercase tracking-wide">Active Contracts</p>
              <p className="text-xl font-bold text-purple-700 mt-1">{amcSummary.activeContracts}</p>
              <p className="text-xs text-gray-400 mt-0.5">sites under AMC</p>
            </div>
          </div>
        </div>
      )}

      {/* ── AMC Due Section ───────────────────────────────────────────────── */}
      {!amcLoading && (amcDue.length > 0 || amcSoon.length > 0) && (
        <div className="bg-white rounded-xl shadow p-4 space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">🔔 AMC Invoices Pending</h2>
            <button onClick={() => navigate("/amc-contracts")}
              className="text-blue-600 text-sm hover:underline">
              Manage Contracts →
            </button>
          </div>

          {amcDue.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-2">
                ⚠ Overdue / Due Today ({amcDue.length})
              </p>
              <div className="space-y-2">
                {amcDue.map(c => (
                  <AMCDueCard key={c.id} contract={c} urgent
                    onGenerate={() => navigate(`/billing/create?amc=${c.id}`)} />
                ))}
              </div>
            </div>
          )}

          {amcSoon.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-2">
                📅 Due Within 7 Days ({amcSoon.length})
              </p>
              <div className="space-y-2">
                {amcSoon.map(c => (
                  <AMCDueCard key={c.id} contract={c} urgent={false}
                    onGenerate={() => navigate(`/billing/create?amc=${c.id}`)} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {amcLoading && (
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-sm text-gray-400">Checking AMC contracts...</p>
        </div>
      )}

      {/* Recent Invoices */}
      <div className="bg-white rounded-xl shadow p-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Recent Invoices</h2>
          <button onClick={() => navigate("/billing/invoices")}
            className="text-blue-600 text-sm hover:underline">View All →</button>
        </div>

        {loading ? <p className="text-sm text-gray-400">Loading...</p> :
         recentInvoices.length === 0 ? (
          <p className="text-gray-500 text-sm">No invoices yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="p-3 text-left border-b">Invoice No</th>
                  <th className="p-3 text-left border-b">Client</th>
                  <th className="p-3 text-left border-b">Date</th>
                  <th className="p-3 text-left border-b">Amount</th>
                  <th className="p-3 text-left border-b">Status</th>
                  <th className="p-3 text-left border-b">Action</th>
                </tr>
              </thead>
              <tbody>
                {recentInvoices.map(inv => (
                  <tr key={inv.id} className="hover:bg-gray-50">
                    <td className="p-3 border-b font-medium">
                      {inv.invoice_no}
                      {inv.is_amc && (
                        <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">
                          AMC
                        </span>
                      )}
                    </td>
                    <td className="p-3 border-b">{inv.client_name}</td>
                    <td className="p-3 border-b text-gray-500">
                      {inv.invoice_date?.seconds
                        ? new Date(inv.invoice_date.seconds * 1000).toLocaleDateString("en-IN")
                        : "-"}
                    </td>
                    <td className="p-3 border-b">₹{Number(inv.total_amount || 0).toLocaleString()}</td>
                    <td className="p-3 border-b">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusBadge(inv.payment_status)}`}>
                        {inv.payment_status}
                      </span>
                    </td>
                    <td className="p-3 border-b">
                      <button onClick={() => navigate(`/billing/invoice/${inv.id}`)}
                        className="text-blue-600 hover:underline text-xs">View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── AMC Due Card ─────────────────────────────────────────────────────────────
function AMCDueCard({ contract: c, urgent, onGenerate }) {
  return (
    <div className={`flex items-center justify-between p-3 rounded-xl border ${
      urgent ? "border-red-200 bg-red-50" : "border-blue-100 bg-blue-50"
    }`}>
      <div>
        <p className="text-sm font-semibold text-slate-800">
          {c.contract_no} — {c.client_name}
        </p>
        <p className="text-xs text-slate-500 mt-0.5">
          {c.site_name && `${c.site_name} • `}
          {c.frequency?.charAt(0).toUpperCase() + c.frequency?.slice(1)} •
          ₹{Number(c.amount || 0).toLocaleString("en-IN")}
        </p>
        <p className={`text-xs font-semibold mt-0.5 ${urgent ? "text-red-600" : "text-blue-600"}`}>
          Due: {c.next_invoice_date}
        </p>
      </div>
      <button onClick={onGenerate}
        className={`px-3 py-2 rounded-xl text-xs font-semibold text-white transition shrink-0 ml-3 ${
          urgent ? "bg-red-500 hover:bg-red-600" : "bg-blue-500 hover:bg-blue-600"
        }`}>
        🧾 Generate Invoice
      </button>
    </div>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ title, value, color = "blue" }) {
  const colors = {
    blue:   "border-blue-200 bg-blue-50",
    green:  "border-green-200 bg-green-50",
    yellow: "border-yellow-200 bg-yellow-50",
    red:    "border-red-200 bg-red-50",
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color] || colors.blue}`}>
      <p className="text-gray-500 text-xs uppercase tracking-wide">{title}</p>
      <h3 className="text-xl font-bold mt-1">{value}</h3>
    </div>
  );
}
